import { HomeWork } from '@material-ui/icons';
import { CsvImportButton } from './CsvImportButton';
import {
  defaultAccountSettings,
  defaultMediaFirstSettings,
  useCreateOperator,
} from '../../contexts/CreateOperatorContext';
import { OperatorImport } from './Settings/csvImports';
import React from 'react';

export const Header = () => {
  const { branches, operator, setOperator } = useCreateOperator();
  const classes = useStyles();

  const branchNames = branches.map((branch) => branch.name);

  return (
    <div className={classes.container}>
      <HomeWork fontSize="large" />
      <h1 className={classes.header}>Create Residential Operator</h1>
      <CsvImportButton<OperatorImport>
        canOverwrite={false}
        settingName="Operator Settings"
        requiredColumns={[
          {
            name: 'operatorId',
            type: 'number',
          },
          {
            name: 'operatorName',
            type: 'string',
          },
          {
            name: 'operatorCode',
            type: 'string',
          },
          {
            name: 'timeZone',
            type: 'string',
          },
          {
            name: 'mediaroom',
            type: 'boolean',
          },
          {
            name: 'mediaFirst',
            type: 'boolean',
          },
          {
            name: 'defaultCreditLimit',
            type: 'number',
            nullable: true,
          },
          {
            name: 'creditResetDay',
            type: 'number',
            nullable: true,
          },
          {
            name: 'enhancedData',
            type: 'boolean',
            nullable: true,
          },
          {
            name: 'gratisAccounts',
            type: 'number',
            nullable: true,
          },
          {
            name: 'appKeyUrl1',
            type: 'string',
            nullable: true,
          },
          {
            name: 'appKeyUrl2',
            type: 'string',
            nullable: true,
          },
          {
            name: 'appKeyUrl3',
            type: 'string',
            nullable: true,
          },
          {
            name: 'appKeyUrl4',
            type: 'string',
            nullable: true,
          },
          {
            name: 'useScreensaver',
            type: 'boolean',
            nullable: true,
          },
          {
            name: 'subscribedChannelView',
            type: 'boolean',
            nullable: true,
          },
          {
            name: 'branch',
            options: branchNames,
            type: 'string',
            nullable: false,
            note: 'Required for Mediaroom',
            validator: (value, row) => ({
              isValid: /true/i.test(row.mediaroom) //regex case-insensitive match
                ? Boolean((value ?? '').length) &&
                  branchNames.includes(value.trim().toUpperCase())
                : true,
              message: `A valid branch must be provided (${branchNames.join(
                ','
              )})`,
            }),
          },
          {
            name: 'mediaFirstStreams',
            type: 'number',
            nullable: true,
          },
          {
            name: 'tenantId',
            type: 'string',
            nullable: true,
            note: 'Required for MediaFirst',
            validator: (value, row) => ({
              isValid: /true/i.test(row.mediaFirst) //regex case-insensitive match
                ? Boolean((value ?? '').length)
                : true,
              message: `A MediaFirst Tenant ID must be provided`,
            }),
          },

          {
            name: 'subTenantId',
            type: 'string',
            nullable: true,
          },
          {
            name: 'usesTgOAuth',
            type: 'boolean',
            nullable: true,
          },
          {
            name: 'mediaFirstRecorderId',
            type: 'string',
            nullable: true,
          },
        ]}
        onImport={(csv) => {
          setOperator(mapImportedOperator(csv[0], operator, branches));
        }}
      />
    </div>
  );
};

const useStyles = () => ({
  container:
    'inline-flex items-end justify-between py-4 px-2 w-full border-b border-border-light dark:border-border-dark',
  header: 'text-4xl',
});

const mapImportedOperator = (
  csv: OperatorImport,
  operator: CreateOperator,
  branches: OperatorBranch[]
): CreateOperator =>
  Object.assign(operator, {
    id: csv.operatorId,
    name: csv.operatorName,
    code: csv.operatorCode,
    timeZone: csv.timeZone,
    accountSettings: {
      creditResetDay:
        csv.creditResetDay ?? defaultAccountSettings.creditResetDay,
      defaultCreditLimit:
        csv.defaultCreditLimit ?? defaultAccountSettings.defaultCreditLimit,
      gratisAccounts:
        csv.gratisAccounts ?? defaultAccountSettings.gratisAccounts,
      enhancedData: csv.enhancedData ?? defaultAccountSettings.enhancedData,
    },
    mediaRoomSettings: csv.mediaroom
      ? {
          branch:
            branches.find(
              (branch) => branch.name.toLowerCase() === csv.branch.toLowerCase()
            )?.id ?? null,
          appKeyUrl1: csv.appKeyUrl1,
          appKeyUrl2: csv.appKeyUrl2,
          appKeyUrl3: csv.appKeyUrl3,
          appKeyUrl4: csv.appKeyUrl4,
          useScreensaver: csv.useScreensaver,
          showSubscribedChannelsOnly: csv.subscribedChannelView,
        }
      : undefined,
    mediaFirstSettings: csv.mediaFirst
      ? Object.assign(operator.mediaFirstSettings!, {
          tenantId: csv.tenantId ?? defaultMediaFirstSettings.tenantId,
          subTenantId: csv.subTenantId ?? defaultMediaFirstSettings.subTenantId,
          usesTgAuth: csv.usesTgOAuth ?? defaultMediaFirstSettings.usesTgAuth,
          recorderId:
            csv.mediaFirstRecorderId ?? defaultMediaFirstSettings.recorderId,
          defaultDeviceStreamCount:
            csv.mediaFirstStreams ??
            defaultMediaFirstSettings.defaultDeviceStreamCount,
        })
      : undefined,
  });
